import { useSyncExternalStore } from 'react';
import { getSession, subscribeSession, type SessionInfo } from './auth';
import { getIntegrationConfig, subscribeIntegrationConfig, type IntegrationConfig } from './integrations';

// The React bindings for the two platform reads. Not authored surface: restored from the template
// on every sync, and unwritable under `src/lib/platform/`.
//
// `useSyncExternalStore` rather than a store library, so this file needs nothing installed and
// composes with whatever the app authors for its own state. Each store's SUBSCRIBE is what starts
// it — mounting the first component that reads the session is what probes it — so there is no
// provider to mount, no boot order to get right, and an app that never asks never fetches.

/**
 * The session, live. `{ status: 'unknown' }` on the very first render, then `authed`, `anonymous`
 * or `unavailable` — and `unavailable` means the backend never answered, which is an error state,
 * NOT a reason to send anyone to sign in.
 */
export function useSession(): SessionInfo {
  return useSyncExternalStore(subscribeSession, getSession, getSession);
}

const NO_ROLES: string[] = [];

/**
 * The signed-in person's role slugs — already split out of the comma-joined `user.role` the server
 * stores. `[]` when signed out, still loading, or holding no app role.
 */
export function useSessionRoles(): string[] {
  return useSession().user?.roles ?? NO_ROLES;
}

/** Every publishable integration key this workspace has connected. `{}` until the read lands. */
export function useIntegrationConfig(): IntegrationConfig {
  return useSyncExternalStore(subscribeIntegrationConfig, getIntegrationConfig, getIntegrationConfig);
}

/**
 * One publishable key, or `undefined` until it lands (and forever when nothing is connected).
 * Render the widget only once it is a string: passing `undefined` to a vendor provider is how a
 * map turns into a grey box with a console error instead of an honest "not connected" state.
 */
export function useIntegrationField(system: string, fieldKey: string): string | undefined {
  return useIntegrationConfig()[system]?.[fieldKey];
}
